import classnames from 'classnames';
import React, {
  FunctionComponent,
  ReactElement,
  useState,
  useRef,
  useEffect,
} from 'react';
import Image from 'next/legacy/image';
import arrowRight from 'public/images/arrow-rightV2.svg';
import { useHorizontalScrollFade } from 'hooks/useHorizontalScrollFade';
import ScrollFadeV2 from 'componentsV2/ScrollFadeV2';
export type TableColumn = {
  key: string;
  heading?: string;
  headingMobile?: string;
  widthRatio?: number;
  cellRender?: (
    dataRecord: any,
    columnKey: string
  ) => ReactElement | string | number;
};

/**
 * data records must have distintive id
 */
export type HasId = {
  id?: number | string;
  [k: string]: any;
};

const TableFloorplansV2: FunctionComponent<{
  columns: TableColumn[];
  data: HasId[];
  openFloorplanDialog: (rowIndex: number) => void;
}> = ({ columns, data, openFloorplanDialog }) => {
  function cellValue(value: any) {
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    return value;
  }
  const [allFloorplans, setAllFloorplans] = useState(false);
  const floorplans = allFloorplans ? data : data.slice(0, 4);
  const { ref: scrollContainerRef, showFade } =
    useHorizontalScrollFade<HTMLDivElement>(floorplans.length);
  const headerRefs = useRef<Set<HTMLDivElement>>(new Set());

  useEffect(() => {
    const container = scrollContainerRef.current;

    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement;

      let isHeader = false;
      let currentElement: HTMLElement | null = target;
      while (currentElement && currentElement !== container) {
        if (headerRefs.current.has(currentElement as HTMLDivElement)) {
          isHeader = true;
          break;
        }
        currentElement = currentElement.parentElement;
      }
      if (isHeader) {
        e.preventDefault();

        const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);
        if (isHorizontalScroll && e.deltaX !== 0) {
          if (container) {
            container.scrollLeft += e.deltaX;
          }
        } else if (e.deltaY !== 0) {
          window.scrollBy({ top: e.deltaY, behavior: 'auto' });
        }
      }
    };

    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [floorplans, scrollContainerRef]);

  function renderTableWithStickyHeader() {
    return (
      <div className="flex flex-row w-full">
        {columns.map((column, columnIndex) => {
          const isLastColumn = columnIndex === columns.length - 1;
          return (
            <div
              key={columnIndex}
              className="pb-2 flex flex-col min-w-0"
              style={{ flexGrow: column.widthRatio || 1, flexBasis: 0 }}
            >
              <div
                ref={(el) => {
                  if (el) headerRefs.current.add(el);
                }}
                className="px-2.5 pb-[5px] flex items-center justify-center border border-beige border-b-gray-darkBorder border-t-0 border-r-0 md:pr-2 md:mb-8 text-16-16-0.3 font-sohneKraftig whitespace-nowrap h-7 mb-0 bg-white z-10 sticky top-0"
                style={{ top: 0, background: 'white' }}
              >
                {column.headingMobile
                  ? column.headingMobile
                  : column.heading || column.key}
              </div>

              {floorplans.map((row, i) => (
                <div key={i} onClick={() => openFloorplanDialog(i)}>
                  <div
                    className={classnames(
                      'p-2.5 border border-t-0 border-beige flex items-center justify-center text-16-16-0.3 md:text-16-26-0.3 tracking-[-0.5px] cursor-pointer',
                      {
                        'border-r-0': !isLastColumn,
                      }
                    )}
                  >
                    <div>
                      {column.cellRender
                        ? column.cellRender(row, column.key)
                        : cellValue(row[column.key])}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className={
            'whitespace-nowrap  overflow-y-auto no-scrollbar overscroll-x-none w-full'
          }
        >
          {renderTableWithStickyHeader()}
        </div>
        <ScrollFadeV2 visible={showFade} />
      </div>

      {data.length > 4 && (
        <button
          onClick={() => setAllFloorplans(!allFloorplans)}
          className="flex items-center mt-4 gap-[6px]  cursor-pointer md:mt-8 md:mb-10 lg:my-6 w-max"
        >
          <span className="whitespace-nowrap  font-semibold">
            {`${allFloorplans ? 'Hide' : 'Show more'}`}
          </span>
          <div className="h-4">
            <Image
              className={classnames({
                'transform rotate-90': !allFloorplans,
                'transform rotate-[270deg]': allFloorplans,
              })}
              src={arrowRight}
              alt="floorplans"
              width={15}
              height={15}
            />
          </div>
        </button>
      )}
    </>
  );
};

export { TableFloorplansV2 };
